import { NewCampaignState } from "@/services/new-campaign.service";
import {
  Configuration,
  NewCampaign,
  NewSchedule,
  NewScheduleChange,
  Schedule,
  ScheduleChange,
  ScheduleChangeUpdate,
  ScheduleUpdate,
  SimpleCampaign,
} from "@/types/campaign";
import { Dispatch, SetStateAction, useCallback } from "react";
import { mapAndExclude } from "./array-utils";
import { DAYS } from "./dates";
import { formatNumber } from "./number-utils";

/**
 * Returns an estimate of a number based on its value, following these rules:
 * - Over 1,000: Round to nearest hundred (e.g., ~1,200)
 * - Under 1,000: Round to nearest hundred (e.g., ~700)
 * - Under 100: Round to nearest ten (e.g., ~70), with a minimum of 10
 * - 0: Returns "0"
 *
 * @param value The number to estimate
 * @returns A string representation of the estimate
 */
export function getEstimate(value: number): string {
  if (value === 0) return "0";

  let rounded = 0;

  if (value < 100) {
    // Round to nearest ten with a minimum of 10
    rounded = Math.max(10, Math.round(value / 10) * 10);
  }
  if (value < 1000) {
    // Round to nearest hundred
    rounded = Math.round(value / 100) * 100;
  } else {
    // Over 1000, round to nearest hundred
    rounded = Math.round(value / 100) * 100;
  }
  return `~${formatNumber(rounded)}`;
}

export function useCampaignConfigurations(
  campaign: NewCampaignState,
): [Configuration[]];

export function useCampaignConfigurations(
  campaign: NewCampaign,
  setCampaign?: Dispatch<SetStateAction<NewCampaign>>,
): [Configuration[], Dispatch<SetStateAction<Configuration[]>>];

export function useCampaignConfigurations(
  campaign: NewCampaignState | NewCampaign,
  setCampaign?: Dispatch<SetStateAction<NewCampaign>>,
) {
  const configurations = campaign.configurations;

  const setConfigurations = useCallback(
    (valueOrSetter: SetStateAction<Configuration[]>) => {
      if (!setCampaign) return;
      setCampaign((prevCampaign) => ({
        ...prevCampaign,
        configurations:
          typeof valueOrSetter === "function"
            ? valueOrSetter(prevCampaign.configurations)
            : valueOrSetter,
      }));
    },
    [setCampaign],
  );

  if (!setCampaign) return [configurations];

  return [configurations, setConfigurations];
}

export function mergeScheduleChangeUpdates(
  currentScheduleChanges: NewScheduleChange[],
  updateScheduleChanges: ScheduleChangeUpdate[],
) {
  const scheduleChangesByDate = new Map<string, NewScheduleChange>(
    currentScheduleChanges.map((change) => [change.date, change]),
  );

  for (const change of updateScheduleChanges) {
    if (change.amount === null) {
      scheduleChangesByDate.delete(change.date);
      continue;
    }

    const existingChange = scheduleChangesByDate.get(change.date);

    const newChange = existingChange
      ? { ...existingChange, amount: change.amount }
      : { date: change.date, amount: change.amount };

    scheduleChangesByDate.set(change.date, newChange);
  }

  return [...scheduleChangesByDate.values()];
}

export function mergeScheduleUpdates(
  currentSchedules: NewSchedule[],
  updateSchedules: Pick<
    ScheduleUpdate,
    "dayOfWeek" | "amount" | "scheduleChanges"
  >[],
) {
  const newSchedules: NewSchedule[] = [];

  const currentSchedulesByDayOfWeek = new Map(
    currentSchedules.map((schedule) => [
      schedule.dayOfWeek.toLowerCase(),
      schedule,
    ]),
  );

  const updateSchedulesByDayOfWeek = new Map(
    updateSchedules.map((schedule) => [
      schedule.dayOfWeek.toLowerCase(),
      schedule,
    ]),
  );

  for (const day of DAYS) {
    const currentSchedule = currentSchedulesByDayOfWeek.get(day.id);
    const updateSchedule = updateSchedulesByDayOfWeek.get(day.id);

    if (currentSchedule) {
      if (updateSchedule) {
        const newSchedule = {
          ...currentSchedule,
          amount: updateSchedule.amount ?? currentSchedule.amount,
          scheduleChanges: updateSchedule.scheduleChanges
            ? mergeScheduleChangeUpdates(
                [...(currentSchedule.scheduleChanges ?? [])],
                updateSchedule.scheduleChanges,
              )
            : currentSchedule.scheduleChanges,
        };

        newSchedules.push(newSchedule);
      } else {
        newSchedules.push(currentSchedule);
      }
    } else if (updateSchedule) {
      newSchedules.push({
        dayOfWeek: day.id,
        amount: updateSchedule.amount ?? 0,
        scheduleChanges: mapAndExclude(
          updateSchedule.scheduleChanges ?? [],
          (exclude, scheduleChange) =>
            scheduleChange.amount === null
              ? exclude
              : { ...scheduleChange, amount: scheduleChange.amount },
        ),
      });
    }
  }

  return newSchedules;
}

export function getFinalCampaignName(
  campaignName: string,
  simpleCampaigns: SimpleCampaign[],
) {
  // Matches names like "Ny kampagne" and "Ny kampagne (123)"
  const regex = /^Ny kampagne( \(([0-9]+)\))?$/;

  // Skip if the campaign has a custom name
  if (campaignName !== "" && !regex.test(campaignName)) return campaignName;

  let newNumberSuffix = 0;

  // Find the highest existing number suffix and increment it
  for (const simpleCampaign of simpleCampaigns) {
    const matches = simpleCampaign.name.match(regex);
    if (!matches) continue;

    const numberSuffix = Number(matches[2] ?? "0");
    if (isNaN(numberSuffix)) continue;

    if (numberSuffix >= newNumberSuffix) {
      newNumberSuffix = numberSuffix + 1;
    }
  }

  let newCampaignName = "Ny kampagne";
  if (newNumberSuffix > 0) {
    newCampaignName += ` (${newNumberSuffix})`;
  }

  return newCampaignName;
}
